/**
 * Idées de SKILLCARDS proposées par l'IA (admin) : l'admin donne un niveau,
 * une rareté et, s'il veut, un thème ; il choisit une ou plusieurs idées et
 * l'app crée les cartes (« Générer la carte » → brouillon → recto).
 * Edge fn `card-ideas-generate` (n'écrit rien). Helpers PURS testés.
 */

import { useMutation } from '@tanstack/react-query';
import { SCHOOL_LEVELS } from '@jeraisonne/db';
import { CARD_RARITY_LABELS, type CardRarity, type CardType } from '@jeraisonne/db/cards';
import { supabase } from '@/lib/supabase';
import { applyGeneratedCardInfo } from './card-info';
import { CardFunctionError, type CardInput, type GeneratedCardInfo } from './api';

/** Miroir de `CardIdea` (_shared/prompts/card-ideas.ts). */
export interface CardIdea {
  title: string;
  subject: string;
  card_type: CardType;
  topic: string;
  pitch: string;
  why: string;
}

export interface CardIdeasRequest {
  level: string;
  rarity: CardRarity;
  theme?: string;
  subject?: string;
  count?: number;
}

export const CARD_IDEA_COUNTS = [4, 6, 8, 10, 12] as const;

const IDEAS_ERROR_MESSAGES: Record<string, string> = {
  budget_exceeded: 'Le budget IA du jour est atteint. Réessaie demain.',
  card_invalid: 'L’IA n’a pas proposé d’idées valides. Réessaie ou précise le thème.',
  forbidden: 'Réservé aux administrateurs.',
  invalid_input: 'Choisis un niveau et une rareté.',
};

export function useCardIdeas() {
  return useMutation({
    mutationFn: async (body: CardIdeasRequest): Promise<CardIdea[]> => {
      const payload = {
        ...body,
        theme: body.theme?.trim() || undefined,
        subject: body.subject || undefined,
      };
      const { data, error } = await supabase.functions.invoke<{ ok: boolean; ideas: CardIdea[] }>(
        'card-ideas-generate',
        { body: payload },
      );
      if (error) {
        let code: string | undefined;
        let message = error.message;
        const ctx = (error as Error & { context?: Response }).context;
        if (ctx && typeof ctx.json === 'function') {
          try {
            const parsed = (await ctx.json()) as { error?: string; message?: string };
            code = parsed.error;
            message = parsed.message ?? parsed.error ?? message;
          } catch {
            /* message générique */
          }
        }
        throw new CardFunctionError((code && IDEAS_ERROR_MESSAGES[code]) || message, code);
      }
      if (!data?.ideas?.length) throw new CardFunctionError('Aucune idée proposée.');
      return data.ideas;
    },
  });
}

const levelLabel = (level: string) => SCHOOL_LEVELS.find((l) => l.value === level)?.label ?? level;

/** Texte donné à « Générer la carte » pour une idée choisie. */
export function ideaSourceText(
  idea: CardIdea,
  ctx: { level: string; rarity: CardRarity; theme?: string },
): string {
  return [
    `SKILLCARD : ${idea.title}`,
    `Niveau : ${levelLabel(ctx.level)}`,
    `Rareté : ${CARD_RARITY_LABELS[ctx.rarity]}`,
    `Sujet : ${idea.topic}`,
    ctx.theme?.trim() ? `Thème : ${ctx.theme.trim()}` : null,
    `Ce que la carte enseigne : ${idea.pitch}`,
  ]
    .filter(Boolean)
    .join('\n');
}

/**
 * Carte à créer depuis une idée : la proposition complète de l'IA, mais le
 * NIVEAU et la RARETÉ choisis par l'admin priment (le niveau est toujours
 * présent, en premier), ainsi que le titre, le type et la matière de l'idée.
 */
export function cardInputFromIdea(
  empty: CardInput,
  info: GeneratedCardInfo,
  idea: CardIdea,
  ctx: { level: string; rarity: CardRarity; theme?: string },
): CardInput {
  const base = applyGeneratedCardInfo(empty, info);
  const levels = [ctx.level, ...base.levels.filter((l) => l !== ctx.level)];
  return {
    ...base,
    title: idea.title.slice(0, 120),
    card_type: idea.card_type,
    subject: idea.subject,
    levels,
    rarity: ctx.rarity,
    source_text: ideaSourceText(idea, ctx),
    lesson_ids: [],
  };
}

/** Étapes de création d'une carte depuis une idée (affichage de la progression). */
export type IdeaJobStep = 'waiting' | 'info' | 'create' | 'recto' | 'done' | 'error';

export const IDEA_JOB_LABELS: Record<IdeaJobStep, string> = {
  waiting: 'En attente',
  info: 'Préparation de la carte…',
  create: 'Création…',
  recto: 'Rédaction du recto…',
  done: 'Carte créée',
  error: 'Échec',
};
